import { useEffect, useState } from "react";
import { Download, FileText, FileSpreadsheet, FileArchive, File as FileIcon, ShieldAlert, Loader2 } from "lucide-react";

// Helper since we use a specific downloads endpoint
const myDownloadsApi = {
  list: async () => {
    const API = "";
    const res = await fetch(`${API}/api/my/downloads`, { credentials: "include" });
    if (!res.ok) {
      const txt = await res.text();
      throw new Error(`${res.status}: ${txt || res.statusText}`);
    }
    return res.json();
  },
};

const EXT_ICON = {
  pdf: FileText,
  doc: FileText,
  docx: FileText,
  xls: FileSpreadsheet,
  xlsx: FileSpreadsheet,
  csv: FileSpreadsheet,
  zip: FileArchive,
  rar: FileArchive,
};

function formatBytes(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function MyDownloads() {
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await myDownloadsApi.list();
        setGroups(res.grupos || []);
      } catch (e) {
        setError(e.message || "Erro ao carregar");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="max-w-5xl" data-testid="my-downloads-loading">
        <h1 className="text-2xl text-[#0F1829] mb-4" style={{ fontFamily: "'Fraunces', serif", fontWeight: 600 }}>Downloads</h1>
        <div className="flex items-center gap-2 text-[#978D78] text-sm">
          <Loader2 size={14} className="animate-spin" /> A carregar...
        </div>
      </div>
    );
  }

  if (error) {
    const forbidden = error.includes("403") || error.toLowerCase().includes("exclusiva");
    return (
      <div className="max-w-5xl" data-testid="my-downloads-error">
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-6 flex items-start gap-3">
          <ShieldAlert size={20} className="text-amber-600 shrink-0 mt-0.5" />
          <div>
            <h2 className="text-lg font-semibold text-amber-900" style={{ fontFamily: "'Fraunces', serif" }}>
              {forbidden ? "Área exclusiva para consultores" : "Não foi possível carregar"}
            </h2>
            <p className="text-sm text-amber-800 mt-1">
              {forbidden
                ? "Esta zona de downloads está apenas disponível para consultores IHTP. Se é consultor e está a ver este aviso, contacte o suporte."
                : error}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const totalDocs = groups.reduce((n, g) => n + (g.documentos || []).length, 0);

  return (
    <div className="max-w-5xl" data-testid="my-downloads-page">
      <div className="mb-6">
        <h1 className="text-2xl text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 600 }}>
          Downloads
        </h1>
        <p className="text-sm text-[#6B7280] mt-1">
          Documentos disponíveis para consultores — catálogos, templates, materiais de apoio.
          <span className="ml-2 text-[#978D78]">({totalDocs} documentos em {groups.length} categorias)</span>
        </p>
      </div>

      <div className="space-y-6">
        {groups.map((g) => (
          <section key={g.tipo_id || g.tipo} data-testid={`dl-group-${g.tipo_id || g.tipo}`}>
            <h2 className="text-xs uppercase tracking-[0.2em] text-[#978D78] font-bold mb-3">
              {g.tipo}
              <span className="ml-2 text-[10px] text-[#978D78] font-normal normal-case tracking-normal">
                ({g.documentos.length})
              </span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {g.documentos.map((doc) => {
                const Icon = EXT_ICON[(doc.ext || "").toLowerCase()] || FileIcon;
                const disabled = !doc.url;
                return (
                  <a
                    key={doc.id}
                    href={doc.url || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`dl-${doc.id}`}
                    onClick={(e) => disabled && e.preventDefault()}
                    className={`group rounded-xl border border-[#E6E1D6] bg-white p-4 flex items-start gap-3 transition ${
                      disabled ? "opacity-50 cursor-not-allowed" : "hover:border-[#0F1829] hover:shadow-sm"
                    }`}
                  >
                    <div className="w-10 h-10 rounded-lg bg-[#F5F2EB] grid place-items-center text-[#978D78] shrink-0 group-hover:bg-[#0F1829] group-hover:text-white transition">
                      <Icon size={18} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-[#0F1829] font-medium leading-tight">{doc.titulo}</p>
                      <p className="text-[11px] text-[#978D78] mt-1">
                        {doc.ext ? doc.ext.toUpperCase() : "Ficheiro"}
                        {doc.tamanho_bytes ? ` • ${formatBytes(doc.tamanho_bytes)}` : ""}
                        {doc.data ? ` • ${doc.data}` : ""}
                      </p>
                    </div>
                    {!disabled && (
                      <Download size={14} className="text-[#978D78] shrink-0 mt-1 group-hover:text-[#0F1829]" />
                    )}
                  </a>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
